import React, { useState } from 'react';
import { 
  MessageSquare, 
  ChevronDown,
  ChevronRight,
  Edit3,
  Trash2,
  X
} from 'lucide-react';
import { Button } from './ui';
import { Task } from '../types';
import { formatRelativeTime, getStatusColor, getPriorityColor } from '../utils/format';
import { TaskFiles } from './TaskFiles';
import { AddLink } from './AddLink';
import { RichTextDisplay } from './RichTextDisplay';
import { cn } from '../utils/cn';
import { useUser } from '../contexts/UserContext';

interface TaskCardProps {
  task: Task;
  onStatusChange: (taskId: string, status: Task['status']) => void;
  onAddComment: (taskId: string, content: string, author: string) => void;
  onDeleteComment?: (taskId: string, commentId: string) => void;
  onAddFile?: (taskId: string, file: import('../types').TaskFile) => void;
  onDeleteFile?: (taskId: string, fileId: string) => void;
  onEditTask?: (task: Task) => void;
  onDeleteTask?: (taskId: string) => void;
  className?: string;
}

export function TaskCard({ 
  task, 
  onStatusChange, 
  onAddComment,
  onDeleteComment,
  onAddFile,
  onDeleteFile,
  onEditTask,
  onDeleteTask,
  className 
}: TaskCardProps) {
  const { user } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);


  const handleStatusChange = (newStatus: Task['status']) => {
    onStatusChange(task.id, newStatus);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setIsAddingComment(true);
    try {
      const author = user?.displayName || user?.email || 'Unknown User';
      await onAddComment(task.id, newComment, author);
      setNewComment('');
      setIsExpanded(true); // Expand to show the new comment
    } finally {
      setIsAddingComment(false);
    }
  };

  const statusOptions: { value: Task['status']; label: string }[] = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' },
  ];

  const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'completed';

  return (
    <div className={cn(
      'bg-white/30 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden',
      isExpanded && 'shadow-lg scale-[1.02]',
      className
    )}>
      <div className="p-6">
        {/* Task Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <h3 className="text-xl font-display font-bold text-gray-900 leading-tight">
                  {task.title}
                </h3>
                {isOverdue && (
                  <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    ⚠️ Overdue
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                {onEditTask && (
                  <button
                    onClick={() => onEditTask(task)}
                    className="w-10 h-10 bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 flex items-center justify-center hover:bg-white/60 hover:text-primary-500 transition-all duration-200"
                    title="Edit task"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
                
                {onDeleteTask && (
                  <button
                    onClick={() => onDeleteTask(task.id)}
                    className="w-10 h-10 bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 flex items-center justify-center hover:bg-white/60 hover:text-red-500 transition-all duration-200"
                    title="Delete task"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-10 h-10 bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 flex items-center justify-center hover:bg-white/60 transition-all duration-200"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="text-gray-600 font-text leading-relaxed">
              <RichTextDisplay content={task.description} />
            </div>
          </div>
        </div>

        {/* Task Status & Metadata */}
        <div className="space-y-4">
          {/* Status Badges */}
          <div className="flex flex-wrap items-center gap-3">
            <div className={cn(
              'px-4 py-2 rounded-2xl font-semibold text-sm',
              getStatusColor(task.status)
            )}>
              {statusOptions.find(s => s.value === task.status)?.label}
            </div>
            
            <div className={cn(
              'px-4 py-2 rounded-2xl font-semibold text-sm',
              getPriorityColor(task.priority)
            )}>
              {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
            </div>
            

            
            {(task.comments?.length || 0) > 0 && (
              <div className="flex items-center px-3 py-2 bg-gray-100 text-gray-600 rounded-2xl text-sm font-semibold">
                <MessageSquare className="w-4 h-4 mr-2" />
                {task.comments?.length || 0} comments
              </div>
            )}
          </div>

          {/* Google Drive Files Section - Always Visible */}
          {(task.files?.length || 0) > 0 && (
            <div className="mt-4">
              <TaskFiles 
                files={task.files} 
                onDeleteFile={onDeleteFile ? (fileId) => onDeleteFile(task.id, fileId) : undefined}
              />
            </div>
          )}

        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-6">
            {/* Add Link Section */}
            {onAddFile && (
              <div className="space-y-4">
                <AddLink 
                  onAddFile={(file) => onAddFile(task.id, file)}
                />
              </div>
            )}

            {/* Status Update Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-display font-bold text-gray-900">Update Status</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {statusOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={task.status === option.value ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => handleStatusChange(option.value)}
                    className={cn(
                      'justify-center',
                      task.status === option.value && 'ring-2 ring-primary-500/50'
                    )}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Comments Section */}
            {(task.comments?.length || 0) > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-display font-bold text-gray-900">
                  Comments ({task.comments?.length || 0})
                </h4>
                <div className="space-y-4">
                  {task.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 p-4"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-3">
                          <span className="font-display font-bold text-gray-900">
                            {comment.author}
                          </span>
                          <span className="text-sm text-gray-500 font-text">
                            {formatRelativeTime(comment.timestamp)}
                          </span>
                        </div>
                        {onDeleteComment && (
                          <button
                            onClick={() => onDeleteComment(task.id, comment.id)}
                            className="w-6 h-6 bg-white/40 backdrop-blur-xl rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white/60 hover:text-red-500 transition-all duration-200"
                            title="Delete comment"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-gray-700 font-text leading-relaxed">{comment.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Comment Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-display font-bold text-gray-900">Add Comment</h4>
              <div className="space-y-4">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share an update, ask a question, or provide feedback..."
                  className="w-full p-4 bg-white/40 backdrop-blur-xl border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-400/50 font-text placeholder-gray-500 resize-none"
                  rows={4}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    loading={isAddingComment}
                    size="md"
                    variant="primary"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Post Comment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
